import type {
  BidsCollection,
  Comment,
  CommentsCollection,
  ContestHandover,
  ContestHandoverDisputesCollection,
  ContestHandoversCollection,
  ContestReviewForEmployer,
  ContestReviewForFreelancer,
  ContestUserHasGivenFeedbackCollection,
  ContestQuickviewEntriesCollection,
  ContestViewEntriesCollection,
  ContestViewEntryIdsCollection,
  DeliveryContactsSensitiveDetailsCollection,
  EnterprisesCollection,
  ExtraFeedPostContext,
  FeedCollection,
  FeedMetaCollection,
  FeedPostsCollection,
  GrantsCollection,
  GroupFeedCollection,
  GroupPermissionsCollection,
  GroupsCollection,
  GroupsSelfCollection,
  MembershipSubscriptionCollection,
  MembershipSubscriptionHistoryCollection,
  ProjectsCollection,
  ProjectTitleEditRequestCollection,
  ProjectViewBidsCollection,
  ProjectViewProjectsCollection,
  ReferralEligibilityCollection,
  ReviewsCollection,
  TextEntity,
  UserHasGivenFeedbackCollection,
  UsersSelfCollection,
} from '@freelancer/datastore/collections';
import {
  bidToProjectViewBidTransformer,
  ContestUserReviewRole,
  EntryIdFilterOption,
  getGroupOrderByFeedReadLatestTimeInMs,
  GrantRole,
  subscriptionToSubscriptionHistoryTransformer,
  UserReviewRole,
} from '@freelancer/datastore/collections';
import { deepSpread } from '@freelancer/datastore/core';
import { generateId } from '@freelancer/datastore/testing/helpers';
import { toNumber } from '@freelancer/utils';
import {
  EntityTypeApi,
  FrontendProjectStatusApi,
  RoleApi,
} from 'api-typings/common/common';
import {
  FeedContextTypeApi,
  FeedReferenceTypeApi,
} from 'api-typings/feed/feed';
import { GrantedPermissionApi } from 'api-typings/grants/grants';
import {
  GroupMemberRoleApi,
  GroupPermissionApi,
} from 'api-typings/groups/groups';
import {
  BidCompleteStatusApi,
  ProjectStatusApi,
  ProjectSubStatusApi,
  ProjectTitleEditRequestStatusApi,
} from 'api-typings/projects/projects';
import { addMutationPropagator } from './document-creators';

export function addMutationPropagators(): void {
  // Projects that get posted in the PJP are pushed to ProjectViewProjects,
  // and we want to propagate them to Projects as well for other pages
  addMutationPropagator<ProjectViewProjectsCollection, ProjectsCollection>({
    from: 'projectViewProjects',
    to: 'projects',
    config: {
      // no-op as ProjectViewProject is a superset of Project
      push: (_, project) => project,
    },
  });

  // The end project modal updates the `bids` collection, which in the real
  // datastore also updates the corresponding bid in `projectViewProject.selectedBids`.
  // Since the fake datastore has no reducer layer, we need to recreate this
  // behaviour with a mutation propagator.
  addMutationPropagator<BidsCollection, ProjectViewProjectsCollection>({
    from: 'bids',
    to: 'projectViewProjects',
    config: {
      update: {
        // The ID of the `projectViewProject` to update
        targetDocumentId: bid => bid.projectId,
        // A function that updates the ProjectViewProject appropriately, in this
        // case by merging in the delta for the selected bid
        transformer: (authUid, delta, bid, projectViewProject) => ({
          ...projectViewProject,
          selectedBids: [
            ...projectViewProject.selectedBids.map(selectedBid =>
              selectedBid.id === bid.id ? deepSpread(bid, delta) : selectedBid,
            ),
          ],
          frontendProjectStatus:
            delta.completeStatus === BidCompleteStatusApi.COMPLETE ||
            delta.completeStatus === BidCompleteStatusApi.INCOMPLETE
              ? FrontendProjectStatusApi.COMPLETE
              : projectViewProject.frontendProjectStatus,

          ...(delta.completeStatus === BidCompleteStatusApi.COMPLETE
            ? {
                status: ProjectStatusApi.CLOSED,
                subStatus: ProjectSubStatusApi.CLOSED_AWARDED,
              }
            : delta.completeStatus === BidCompleteStatusApi.INCOMPLETE
              ? {
                  status: ProjectStatusApi.CLOSED,
                  subStatus: ProjectSubStatusApi.CANCEL_SELLER,
                }
              : {}),
        }),
      },
    },
  });

  addMutationPropagator<FeedPostsCollection, GroupFeedCollection>({
    from: 'feedPosts',
    to: 'groupFeed',
    config: {
      update: {
        targetDocumentFilter: (feedPost, groupFeed) =>
          groupFeed.feedItemReferenceId === feedPost.id &&
          groupFeed.feedItemReferenceType === FeedReferenceTypeApi.POST,
        transformer: (authUid, delta, feedPost, groupFeed) => ({
          ...groupFeed,
          post: deepSpread(feedPost, delta),
          updated: feedPost.updated,
        }),
      },
      push: (_authUid, feedPost, extra?: ExtraFeedPostContext) => ({
        id: generateId(),
        updated: feedPost.updated,
        post: {
          ...feedPost,
          textEntities: (extra?.textEntities as TextEntity[]) ?? [],
        },
        groupId: toNumber(extra?.feedContextId) || generateId(),
        feedItem: {
          updated: feedPost.updated,
          id: generateId(),
          created: feedPost.created,
          feedMetaId: generateId(),
          contextId: toNumber(extra?.feedContextId) || generateId(),
          contextType:
            (extra?.feedContextType as FeedContextTypeApi) ||
            FeedContextTypeApi.GROUP,
          referenceId: feedPost.id,
          referenceType: FeedReferenceTypeApi.POST,
        },
        permissions: [GroupPermissionApi.FEED_GET],
        postOwnerGroupMember: {
          id: generateId(),
          userId: _authUid,
          groupId: toNumber(extra?.feedContextId) || generateId(),
          permissions: [GroupPermissionApi.FEED_GET],
          role: GroupMemberRoleApi.MEMBER,
          created: Date.now(),
          memberId: generateId(),
          isRemoved: false,
        },
        viewingUserGroupMember: {
          id: generateId(),
          userId: _authUid,
          groupId: toNumber(extra?.feedContextId) || generateId(),
          permissions: [GroupPermissionApi.FEED_GET],
          role: GroupMemberRoleApi.MEMBER,
          created: Date.now(),
          memberId: generateId(),
          isRemoved: false,
        },
        feedItemReferenceType: FeedReferenceTypeApi.POST,
        feedItemReferenceId: feedPost.id,
        isBookmarked: false,
      }),
    },
  });

  addMutationPropagator<FeedPostsCollection, FeedCollection>({
    from: 'feedPosts',
    to: 'feed',
    config: {
      update: {
        targetDocumentFilter: (feedPost, feed) =>
          feed.referenceId === feedPost.id &&
          feed.referenceType === FeedReferenceTypeApi.POST,
        transformer: (authUid, delta, feedPost, feed) => ({
          ...feed,
          updated: feedPost.updated,
        }),
      },
      push: (_authUid, feedPost, extra?: ExtraFeedPostContext) => ({
        updated: feedPost.updated,
        created: feedPost.created,
        feedMetaId: generateId(),
        id: generateId(),
        contextType:
          (extra?.feedContextType as FeedContextTypeApi) ||
          FeedContextTypeApi.GROUP,
        contextId: toNumber(extra?.feedContextId) || generateId(),
        referenceType: FeedReferenceTypeApi.POST,
        referenceId: feedPost.id,
      }),
    },
  });

  // When a comment is created (pushed), update the corresponding groupFeed item to include
  // the new comment in its embedded comments structure.
  addMutationPropagator<CommentsCollection, GroupFeedCollection>({
    from: 'comments',
    to: 'groupFeed',
    config: {
      // No push handler - pushRaw bypasses mutation propagators anyway.
      // We rely on the update handler which is triggered when comments are updated.
      update: {
        targetDocumentFilter: (comment, groupFeed) =>
          groupFeed.post.commentFeedId === comment.threadId,
        transformer: (authUid, delta, comment, groupFeed) => {
          // When a comment is pushed, it triggers an update (e.g., to set the ID).
          // This matches the reducer's API_PUSH_SUCCESS behavior which calls addCommentToGroupFeedItem.
          // Check if this is a new comment being added (not a deletion)
          if (!delta.removeReason) {
            // Match the logic from addCommentToGroupFeedItem (group-feed.transformers.ts)
            const currentComments = groupFeed.comments;
            if (currentComments) {
              // Check if comment already exists (by ID, or by matching threadId + timeCreated + fromUser for id: 0 cases)
              // We need to match comments that were added with id: 0 and are now getting their real ID
              const existingCommentIndex = currentComments.comments.findIndex(
                c => {
                  // Exact ID match (both non-zero)
                  if (c.id === comment.id && c.id !== 0 && comment.id !== 0) {
                    return true;
                  }
                  // Match comment with id: 0 that's getting its real ID
                  // Match by threadId, timeCreated, fromUser, and comment text
                  return (
                    c.id === 0 &&
                    comment.id !== 0 &&
                    c.threadId === comment.threadId &&
                    c.timeCreated === comment.timeCreated &&
                    c.fromUser === comment.fromUser &&
                    c.comment === comment.comment &&
                    c.parentId === comment.parentId
                  );
                },
              );

              if (existingCommentIndex !== -1) {
                // Comment already exists - update it (e.g., when id changes from 0 to real ID)
                const updatedComments = [...currentComments.comments];
                updatedComments[existingCommentIndex] = comment;
                return {
                  ...groupFeed,
                  comments: {
                    ...currentComments,
                    comments: updatedComments as readonly Comment[],
                  },
                };
              }
              if (comment.parentId === undefined) {
                // Top-level comment: add to front of array and increment count
                return {
                  ...groupFeed,
                  comments: {
                    comments: [comment, ...currentComments.comments],
                    count: currentComments.count + 1,
                  },
                };
              }
              // Nested comment: update parent's hasReplies, DON'T add to array, DON'T increment count
              const parentExistsInList = currentComments.comments.some(
                c => c.id === comment.parentId,
              );
              const updatedCommentList = (
                parentExistsInList
                  ? currentComments.comments.map(c =>
                      c.id === comment.parentId
                        ? { ...c, hasReplies: true }
                        : c,
                    )
                  : currentComments.comments.map(c => ({
                      ...c,
                      hasReplies: true,
                    }))
              ) as readonly Comment[];
              return {
                ...groupFeed,
                comments: {
                  ...currentComments,
                  comments: updatedCommentList,
                  // DON'T increment count for nested comments
                  count: currentComments.count,
                },
              };
            }
            // If no comments exist yet, create the structure with the new comment
            // Only add top-level comments to the array
            if (comment.parentId === undefined) {
              return {
                ...groupFeed,
                comments: {
                  comments: [comment],
                  count: 1,
                },
              };
            }
            // Don't add nested comments to the array, but we need to create the structure
            // This is an edge case - normally nested comments come after top-level ones
            return groupFeed;
          }

          // For comment deletions, the reducer uses updateCommentInGroupFeedItem
          // but we'll handle it here for completeness
          if (delta.removeReason && groupFeed.comments) {
            const commentIndex = groupFeed.comments.comments.findIndex(
              c => c.id === comment.id,
            );
            if (commentIndex !== -1) {
              const updatedComments = groupFeed.comments.comments.filter(
                (_, index) => index !== commentIndex,
              );
              return {
                ...groupFeed,
                comments: {
                  ...groupFeed.comments,
                  comments: updatedComments,
                  count: Math.max(0, groupFeed.comments.count - 1),
                },
              };
            }
          }

          // No changes needed
          return groupFeed;
        },
      },
    },
  });

  // When a `membershipSubscription` is created, we also want to create a corresponding
  // `membershipSubscriptionHistory` entry
  addMutationPropagator<
    MembershipSubscriptionCollection,
    MembershipSubscriptionHistoryCollection
  >({
    from: 'membershipSubscription',
    to: 'membershipSubscriptionHistory',
    config: {
      push: subscriptionToSubscriptionHistoryTransformer,
    },
  });

  // When a review is submitted, the `userHasGivenFeedback` collection should be
  // updated with `feedbackLeft: true`. Logic copied from the reducer
  addMutationPropagator<ReviewsCollection, UserHasGivenFeedbackCollection>({
    from: 'reviews',
    to: 'userHasGivenFeedback',
    config: {
      push: (authUid, review) => ({
        id: `${review.context.id}-${review.toUserId}`,
        feedbackLeft: true,
        projectId: review.context.id,
        toUserId: review.toUserId,
        fromUserId: review.fromUserId,
        reviewType:
          review.role === RoleApi.FREELANCER
            ? UserReviewRole.SELLER
            : UserReviewRole.BUYER,
      }),
    },
  });

  // When a review is submitted, the `contestUserHasGivenFeedback` collection should be
  // updated with `feedbackLeft: true`. Logic copied from the reducer
  addMutationPropagator<
    ReviewsCollection,
    ContestUserHasGivenFeedbackCollection
  >({
    from: 'reviews',
    to: 'contestUserHasGivenFeedback',
    config: {
      push: (
        authUid,
        review: ContestReviewForFreelancer | ContestReviewForEmployer,
      ) => ({
        id: `${review.entryId}-${review.toUserId}`,
        feedbackLeft: true,
        entryId: review.entryId ?? generateId(),
        toUserId: review.toUserId,
        fromUserId: review.fromUserId,
        role:
          review.role === RoleApi.FREELANCER
            ? ContestUserReviewRole.FREELANCER
            : ContestUserReviewRole.EMPLOYER,
      }),
    },
  });

  // On a project title edit request, when the client accepts the change
  // it should update the title of the project in the datastore as well.
  addMutationPropagator<
    ProjectTitleEditRequestCollection,
    ProjectViewProjectsCollection
  >({
    from: 'projectTitleEditRequest',
    to: 'projectViewProjects',
    config: {
      update: {
        targetDocumentId: request => request.projectId,
        transformer: (authUid, delta, projectTitleEditRequest, project) => ({
          ...project,
          ...(delta.status === ProjectTitleEditRequestStatusApi.ACCEPTED
            ? { title: projectTitleEditRequest.newTitle }
            : {}),
        }),
      },
    },
  });

  // On editing project contact details, updating the project
  // should also update the sensitive details (name and phone number)
  addMutationPropagator<
    ProjectViewProjectsCollection,
    DeliveryContactsSensitiveDetailsCollection
  >({
    from: 'projectViewProjects',
    to: 'deliveryContactsSensitiveDetails',
    config: {
      update: {
        targetDocumentId: request => request.id,
        transformer: (authUid, delta, project, sensitiveDetails) => ({
          id: project.id,
          pickupDeliveryContactSensitiveDetails: {
            ...project.localDetails?.pickupDeliveryContact,
          },
          dropoffDeliveryContactSensitiveDetails: {
            ...project.localDetails?.dropoffDeliveryContact,
          },
        }),
      },
    },
  });

  // We need this because the bid form pushes to 'bids' but the bid list fetches
  // from 'projectViewBids'. In the real datastore this is solved by a Websocket
  // message being sent for the new bid, and the reducer for both collections
  // listening to it. However, since we use a faked backend in tests we have to
  // replicate the store-level mutations with custom transformers and by
  // specifying related collections.
  addMutationPropagator<BidsCollection, ProjectViewBidsCollection>({
    from: 'bids',
    to: 'projectViewBids',
    config: {
      push: bidToProjectViewBidTransformer,
      // Editing a bid should also edit the projectViewBid. Since no transformer
      // is given, this defaults to merging in the same delta to the projectViewBid
      update: {
        targetDocumentId: bid => bid.id,
      },
    },
  });

  // When a user views a post we updated the feedMeta collection. However, we also need to ensure this updates the group isFeedRead
  // property so that all unread groups content indicators are removed
  addMutationPropagator<FeedMetaCollection, GroupsSelfCollection>({
    from: 'feedMeta',
    to: 'groupsSelf',
    config: {
      update: {
        targetDocumentId: request => request.contextId,
        transformer: (authUid, delta, feedMetaDoc, groupDoc) => ({
          ...groupDoc,
          isFeedRead: true,
          orderByFeedReadLatestTimeInMs: getGroupOrderByFeedReadLatestTimeInMs(
            groupDoc.lastActiveTimeInMs,
          ),
        }),
      },
    },
  });

  // To ensure we can load the Group page after creation, we need to create the associated permission document. Without it,
  // the Feed page guard fails. This blocks the Feed page rendering, and because change detection runs from the root of the DOM tree upward,
  // it also blocks the page component.
  addMutationPropagator<GroupsCollection, GroupPermissionsCollection>({
    from: 'groups',
    to: 'groupPermissions',
    config: {
      push: (authUid, group) => ({
        id: group.id,
        permissions: [GroupPermissionApi.FEED_GET],
      }),
    },
  });

  // To ensure we can load the Partner Dashboard after enterprise creation, we need to add grants to the user
  addMutationPropagator<EnterprisesCollection, GrantsCollection>({
    from: 'enterprises',
    to: 'grants',
    config: {
      push: (authUid, enterprises) => {
        return {
          id: generateId(),
          resource: {
            id: enterprises.id,
            entityType: EntityTypeApi.ENTERPRISE,
          },
          grantingEntity: {
            id: generateId(),
            entityType: EntityTypeApi.ADMIN,
          },
          entity: {
            id: authUid,
            entityType: EntityTypeApi.USER,
          },
          roleId: GrantRole.USER_MANAGEMENT,
          grantedPermissions: [
            GrantedPermissionApi.VIEW_USER_PERSONAL_DETAILS,
            GrantedPermissionApi.CLOSE_USER,
            GrantedPermissionApi.MANAGE_INSOURCE_ROLES,
            GrantedPermissionApi.MANAGE_ENTERPRISE_PAYMENTS,
            GrantedPermissionApi.UPDATE_ENTERPRISE,
          ],
          roleName: 'User Management',
        };
      },
    },
  });

  // When a user verifies their phone number, update the referral eligibility
  addMutationPropagator<UsersSelfCollection, ReferralEligibilityCollection>({
    from: 'usersSelf',
    to: 'referralEligibility',
    config: {
      update: {
        targetDocumentId: userself => userself.id,
        transformer: (authUid, delta, userSelf, eligibility) => {
          return {
            ...eligibility,
            ...(delta.status?.emailVerified ? { isEmailVerified: true } : {}),
            ...(delta.status?.phoneVerified ? { isPhoneVerified: true } : {}),
          };
        },
      },
    },
  });

  addMutationPropagator<
    ContestHandoverDisputesCollection,
    ContestHandoversCollection
  >({
    from: 'contestHandoverDisputes',
    to: 'contestHandovers',
    config: {
      push: (_, dispute) =>
        ({
          id: dispute.handoverId,
          disputeId: dispute.id,
        }) as unknown as ContestHandover,
    },
  });

  // Mirror the production RATE→NO_FEEDBACK reconciliation (see
  // contest-view-entry-ids.reducer.ts) so the fake datastore drops a rated entry
  // from the cached NO_FEEDBACK lists. ContestQuickviewEntry extends
  // ContestViewEntry, so the base type fits both propagators.
  const removeRatedEntryFromNoFeedback = (
    delta: Partial<ContestViewEntriesCollection['DocumentType']>,
    source: ContestViewEntriesCollection['DocumentType'],
    target: ContestViewEntryIdsCollection['DocumentType'],
  ): ContestViewEntryIdsCollection['DocumentType'] => {
    // Only reconcile on a rating update.
    if (delta.rating == null) {
      return target;
    }
    const index = target.entryIds.indexOf(source.id);
    if (index === -1) {
      return target;
    }
    return {
      ...target,
      entryIds: [
        ...target.entryIds.slice(0, index),
        ...target.entryIds.slice(index + 1),
      ],
      entryNumbers: [
        ...target.entryNumbers.slice(0, index),
        ...target.entryNumbers.slice(index + 1),
      ],
    };
  };

  addMutationPropagator<
    ContestViewEntriesCollection,
    ContestViewEntryIdsCollection
  >({
    from: 'contestViewEntries',
    to: 'contestViewEntryIds',
    config: {
      update: {
        targetDocumentFilter: (source, target) =>
          target.contestId === source.contestId &&
          target.filter === EntryIdFilterOption.NO_FEEDBACK,
        transformer: (_authUid, delta, source, target) =>
          removeRatedEntryFromNoFeedback(delta, source, target),
      },
    },
  });

  addMutationPropagator<
    ContestQuickviewEntriesCollection,
    ContestViewEntryIdsCollection
  >({
    from: 'contestQuickviewEntries',
    to: 'contestViewEntryIds',
    config: {
      update: {
        targetDocumentFilter: (source, target) =>
          target.contestId === source.contestId &&
          target.filter === EntryIdFilterOption.NO_FEEDBACK,
        transformer: (_authUid, delta, source, target) =>
          removeRatedEntryFromNoFeedback(delta, source, target),
      },
    },
  });
}
